import { Deduction } from "@/core/entities/deduction";
import { useDeductionApproval } from "@/presentation/deduction/hooks/deduction";
import { Button, Flex, Heading, Icon, Icons, Modal, Paragraph, Textarea, useToast } from "@ir/ui";
import { useCallback, useState } from "react";

interface SendForApprovalModalProps {
    isOpen: boolean;
    onClose: () => void;
    deduction: Deduction;
}

const SendForApprovalModal = ({ isOpen, onClose, deduction }: SendForApprovalModalProps) => {
    const [comment, setComment] = useState("");
    const { isApprovalSending, sendForApproval } = useDeductionApproval();
    const toast = useToast();

    const handleSendApproval = useCallback(async () => {
        if (isApprovalSending) {
            return;
        }

        const response = await sendForApproval(deduction, comment);
        if (response) {
            toast.success({
                content: "Deduction sent for approval",
            });
            onClose();
        } else {
            toast.error({
                content: "Failed to send deduction for approval",
            });
        }
    }, [deduction, comment, isApprovalSending, onClose, sendForApproval, toast]);

    const getHeaderContent = useCallback(() => {
        return (
            <Heading level={6} className="dispute-modal-heading">
                Send for approval
            </Heading>
        );
    }, []);

    const getFooterContent = useCallback(() => {
        return (
            <Flex direction="row">
                <Button variant="clear" color="grey" onPress={onClose} isDisabled={isApprovalSending}>
                    Cancel
                </Button>
                <Button color="primary" onPress={handleSendApproval} isDisabled={isApprovalSending}>
                    {isApprovalSending && (
                        <Icon name={Icons.Loading} slot="start" className="icon-fast-loading" />
                    )}
                    Submit
                </Button>
            </Flex>
        );
    }, [handleSendApproval, onClose, isApprovalSending]);

    return (
        <Modal isOpen={isOpen} onClose={onClose} zIndex={12} headerContent={getHeaderContent()} footerContent={getFooterContent()} classNames={{
            header: "p-4 pb-0",
            body: "p-4",
            footer: "px-4 py-4",
        }}>
            <Paragraph className="mb-2 text-grey-800 text-sm font-bold">Add Comment (optional)</Paragraph>
            <Textarea maxLength={100} showCharCount={true} placeholder="Enter your comments here..." value={comment} onChange={(e) => setComment(e.target.value)} />
        </Modal>
    );
};

export default SendForApprovalModal;
